import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ChevronDown, Hash, Lock, Users } from "lucide-react";
import { api } from "../lib/api";
import { t, trRoomName } from "../lib/i18n";

/**
 * "Rejoindre avec un code" (lobby, under "Créer une partie" / "Jouer en ligne").
 * A pill like its neighbours; tapping it unfolds the field right under it — no
 * popup — exactly like the guest's name field on the home page: the button sits
 * inside the field. The player types the room code shown in a waiting room
 * ("A2498F") or pastes an invite link; the room is looked up as they type
 * (`GET /rooms/find`, see GameService.find_room), then they go to its waiting
 * room, which joins them.
 */
export function JoinCodePanel({ disabled = false, disabledTitle, className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // "idle" | "searching" | "found" | "not_found"
  const [search, setSearch] = useState({ status: "idle", room: null });
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 180);
    else { setQuery(""); setSearch({ status: "idle", room: null }); }
  }, [open]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // Debounced lookup while the player types.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 3) { setSearch({ status: "idle", room: null }); return undefined; }
    setSearch({ status: "searching", room: null });
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/rooms/find?q=${encodeURIComponent(q)}`);
        setSearch({ status: "found", room: data });
      } catch {
        setSearch({ status: "not_found", room: null });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, open]);

  const canJoin = search.status === "found" && !search.room?.started;
  const join = () => {
    if (canJoin) navigate(`/room/${search.room.room_id}`);
  };

  return (
    // w-0 + min-w-full: spans the row without widening it as the field opens.
    <div
      className={`w-0 min-w-full overflow-hidden border transition-[border-radius,background-color,border-color] duration-200 ${
        open
          ? "rounded-2xl border-amber-500/40 bg-slate-900/80 shadow-lg shadow-amber-500/10"
          : "rounded-[22px] border-white/15 bg-white/[0.06] hover:bg-white/10"
      } ${disabled ? "opacity-35" : ""} ${className}`}
    >
      <button
        type="button"
        data-testid="join-code-open-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        aria-expanded={open}
        className="w-full h-8 px-4 flex items-center justify-between gap-1.5 text-sm font-medium text-slate-100 hover:text-white disabled:cursor-not-allowed"
      >
        <Hash className="h-3.5 w-3.5 text-amber-300 shrink-0" />
        {/* Like "Créer une partie" / "Jouer en ligne": icon then words at the start; the arrow at the end */}
        <span className={`flex-1 text-start ${open ? "text-amber-300" : ""}`}>{t("lobby.code.button")}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180 text-amber-300" : "text-slate-400"}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="field"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2">
              {/* Code / link — the button sits inside the field */}
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  data-testid="join-code-input"
                  placeholder={t("lobby.code.placeholder")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && join()}
                  className={`w-full bg-slate-800/50 border rounded-xl px-4 py-2.5 pe-[120px] text-white placeholder-slate-600 text-base outline-none transition-colors ${
                    search.status === "found"
                      ? "border-emerald-500/50"
                      : search.status === "not_found"
                      ? "border-red-500/50"
                      : "border-white/10 focus:border-amber-500/50"
                  }`}
                  autoComplete="off"
                />
                <button
                  type="button"
                  data-testid="join-code-btn"
                  onClick={join}
                  disabled={!canJoin}
                  className="absolute end-1.5 top-1/2 -translate-y-1/2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-lg px-3 py-1.5 transition-all whitespace-nowrap flex items-center gap-1.5"
                >
                  {search.status === "searching"
                    ? <span className="h-3.5 w-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    : t("common.joinArrow")}
                </button>
              </div>

              {/* The room found, or why not */}
              <AnimatePresence mode="wait" initial={false}>
                {search.status === "found" && (
                  <motion.div
                    key="found"
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
                  >
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
                      <Users className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-emerald-200 text-sm font-semibold truncate">{trRoomName(search.room.name)}</div>
                      <div className="text-slate-400 text-xs flex items-center gap-2">
                        <span>{t("common.playersCount", { a: search.room.members, b: search.room.max_players })}</span>
                        {search.room.started && (
                          <span className="text-amber-400 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> {t("lobby.code.inProgress")}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
                {search.status === "not_found" && (
                  <motion.p
                    key="notfound"
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-1.5 text-red-400 text-xs px-1"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {t("lobby.code.notFound")}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default JoinCodePanel;
